import { BannerInlinePlaceholder } from '@tekface/smart-activation';
import {
  LazyImage,
  Overlay,
  ThemeProps,
  YoutubeThumbnail,
  styled,
} from '@teko/ds-desktop';
import {
  Gallery,
  ProductVideo,
  SOURCE_VIDEO,
  SmallImage,
} from '@teko/teko-ecommerce';
import { getTrackingData } from '@teko/tracking';
import { INLINE_BANNER_CLASSNAME } from 'constants/smartBanner';
import { useProductDetailState } from 'contexts';
import React, { useRef, useState } from 'react';
import { ProductImage, Video } from 'types';

export const ProductImages: React.FC = () => {
  const { product, frame, inlineBanners } = useProductDetailState();
  const [showGallery, setShowGallery] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const vidRef = useRef<HTMLVideoElement>(null);

  const images = product?.images || [];
  const videos = product?.videos || [];
  return (
    <div
      {...getTrackingData({
        isTrackContent: true,
        regionName: 'imgProductDetail',
        contentName: 'viewImage',
        target: 'productImage',
        payload: JSON.stringify({
          sku: product?.sellerSku,
          screenName: 'productDetail',
          index: activeIndex,
        }),
      })}
    >
      <div
        style={{
          cursor: 'pointer',
          position: 'relative',
          marginBottom: '0.5rem',
        }}
        className="productDetailPreview" // class for pushdy banner
      >
        {frame && <FrameWrapper frame={frame} />}
        {images || videos ? (
          <>
            <BannerInlinePlaceholder
              banners={inlineBanners}
              className={INLINE_BANNER_CLASSNAME.INLINE_PDP_PREVIEW_DESK}
            />
            {activeIndex < videos.length ? (
              <ProductVideo
                source={videos[activeIndex].source}
                videoProps={{
                  preload: 'none',
                  width: 325,
                  height: 325,
                  playsInline: true,
                  muted: true,
                  autoPlay: true,
                  controls: true,
                  src: videos[activeIndex].url,
                  onClick: () => setShowGallery(true),
                }}
                youtubeFrameProps={{
                  width: 325,
                  height: 325,
                  autoplay: 1,
                }}
                url={videos[activeIndex].url}
              />
            ) : (
              <LazyImage
                contain
                {...images[activeIndex - videos.length]}
                onClick={() => setShowGallery(true)}
                borderRadius={8}
                imageWidth={500}
              />
            )}
          </>
        ) : null}
      </div>
      <WrapperSmallImages>
        {[...videos, ...images]?.slice(0, 6).map((img, index) => {
          if (index < videos.length) {
            if ((img as Video).source === SOURCE_VIDEO.SYSTEM) {
              return (
                <ProductVideo
                  key={index}
                  source={(img as Video).source}
                  url={(img as Video).url}
                  videoProps={{
                    style: {
                      cursor: 'pointer',
                    },
                    ref: vidRef,
                    preload: 'none',
                    width: 50,
                    height: 45,
                    playsInline: true,
                    muted: true,
                    autoPlay: true,
                    onPlayingCapture: () => vidRef.current?.pause(),
                    onClick: () => setShowGallery(true),
                    onMouseEnter: () =>
                      !(index === 5 && images.length > 6) &&
                      setActiveIndex(index),
                  }}
                />
              );
            }
            return (
              <StyledYoutubeThumbnail
                key={index}
                width={45}
                height={45}
                isActive={index === activeIndex}
                youtubeLink={(img as Video).url}
                onClick={() => setShowGallery(true)}
                onMouseEnter={() =>
                  !(index === 5 && images.length > 6) && setActiveIndex(index)
                }
              />
            );
          }
          return (
            <SmallImage
              key={index}
              image={img as ProductImage}
              alt={(img as ProductImage).alt}
              isActive={index === activeIndex}
              onClick={() => setShowGallery(true)}
              onMouseEnter={() =>
                !(index === 5 && images.length > 6) && setActiveIndex(index)
              }
              extra={
                index === 5 && images.length > 6
                  ? `+${images.length - 6}`
                  : undefined
              }
            />
          );
        })}
      </WrapperSmallImages>
      <Overlay show={showGallery} onEscPressed={() => setShowGallery(false)}>
        {images ? (
          <Gallery
            images={images}
            videos={videos?.map((video) => {
              return { ...video, src: video.url };
            })}
            bgColor="white"
            onClose={() => setShowGallery(false)}
            activeImageIndex={activeIndex}
          />
        ) : null}
      </Overlay>
    </div>
  );
};

const WrapperSmallImages = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 0.5rem;
`;

interface FrameWrapperStyledProps {
  frame: string;
}

interface YoutubeThumbnailStyledProps extends ThemeProps {
  isActive: boolean;
}

const FrameWrapper = styled.div<FrameWrapperStyledProps>(
  ({ frame }: FrameWrapperStyledProps) => `
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  display: flex;
  flex-wrap: wrap;
  background-image: url(${frame});
  background-size: cover;
  height: 100%;
  width: 100%;
  cursor: pointer;
  `,
);

const StyledYoutubeThumbnail = styled(
  YoutubeThumbnail,
)<YoutubeThumbnailStyledProps>`
  border: 1px solid
    ${({ isActive, theme }) =>
      isActive ? theme?.colors.primary500 : 'transparent'};
`;
